import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/header/header';
import './connect.scss';
import { motion, AnimatePresence } from "motion/react";
import emailjs from '@emailjs/browser';

const CHANNEL_ID = 'UCZT2mV56in8aaVDESac4tug'; // e.g. UCxxxxxxxxxx — replace this!
const API_KEY = process.env.REACT_APP_YT_API_KEY;

const Connect = () => {

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [popupMessage, setPopupMessage] = useState(null);
  const [popupType, setPopupType] = useState('success'); // 'success' or 'error'

  // YouTube live state
  const [liveVideoId, setLiveVideoId] = useState(null);
  const [fallbackVideoId, setFallbackVideoId] = useState('w3qIqipVzdU');
  const [isLive, setIsLive] = useState(false);
  const [liveChecked, setLiveChecked] = useState(false);

  useEffect(() => {
    const checkLiveStatus = async () => {
      try {
        // 1. Check for active live broadcast
        const liveRes = await fetch(
          `https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${CHANNEL_ID}&eventType=live&type=video&key=${API_KEY}`
        );
        const liveData = await liveRes.json();

        if (liveData.items && liveData.items.length > 0) {
          setLiveVideoId(liveData.items[0].id.videoId);
          setIsLive(true);
        } else {
          // 2. Fetch latest uploaded video as fallback
          const fallbackRes = await fetch(
            `https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${CHANNEL_ID}&order=date&type=video&maxResults=1&key=${API_KEY}`
          );
          const fallbackData = await fallbackRes.json();
          if (fallbackData.items && fallbackData.items.length > 0) {
            setFallbackVideoId(fallbackData.items[0].id.videoId);
          }
          setIsLive(false);
        }
      } catch (err) {
        console.error('YouTube live check failed:', err);
        setIsLive(false);
      } finally {
        setLiveChecked(true);
      }
    };

    checkLiveStatus();
    // Re-check every 5 minutes
    const interval = setInterval(checkLiveStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const currentVideoId = isLive ? liveVideoId : fallbackVideoId;

  const sendEmail = (e) => {
    e.persist();
    e.preventDefault();
    setIsSubmitting(true);
    emailjs
      .sendForm(
        process.env.REACT_APP_SERVICE_ID,
        process.env.REACT_APP_TEMPLATE_ID,
        e.target,
        process.env.REACT_APP_PUBLIC_KEY
      )
      .then(
        () => {
          setPopupType('success');
          setPopupMessage('Message sent successfully! We\'ll get back to you shortly.');
          setIsSubmitting(false);
          e.target.reset();
        },
        () => {
          setPopupType('error');
          setPopupMessage('Something went wrong. Please try again later.');
          setIsSubmitting(false);
        }
      );
  };

  const closePopup = () => setPopupMessage(null);

  const MotionLink = motion(Link);

  return (
    <div className="connect">
      <Header />

      <section className="hero2">
        <div className="hero-content2">
          <h1>Let's Connect</h1>
          <p>We'd love to hear from you. Whether you have questions, need prayer, or want to get involved, we're here for you.</p>
        </div>
      </section>

      {/* Join Us Live */}
      <motion.section className="live-section">
        <h2 className="section-title">Join Us Live</h2>
        <p className="section-subtitle">
          {isLive
            ? 'We\'re live right now — join us for worship!'
            : 'Can\'t make it in person? Watch our latest message or join our next live service.'}
        </p>

        <motion.div className="video-container">

          {/* Live badge */}
          {isLive && (
            <div className="live-badge">
              <span className="live-dot" />
              LIVE NOW
            </div>
          )}

          {/* Latest video label (when not live) */}
          {/* {!isLive && liveChecked && (
            <p className="latest-video-label">📺 Latest Message</p>
          )} */}

          {/* Loading state */}
          {!liveChecked && (
            <div className="video-loading">Loading...</div>
          )}

          {liveChecked && (
            <iframe
              width="100%"
              height="450"
              src={`https://www.youtube.com/embed/${currentVideoId}${isLive ? '?autoplay=1' : ''}`}
              title={isLive ? 'YouTube Live Stream' : 'Latest YouTube Video'}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          )}
        </motion.div>

        <div className="live-links">
          {[
            {
              icon: "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z",
              text: "Watch on YouTube",
              link: "https://www.youtube.com/@agcmiddletown"
            },
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 + (index * 0.1) }}
            >
              <Link to={item.link} target="_blank" className="live-link">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d={item.icon}/>
                </svg>
                {item.text}
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Service Times */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6 }}
        className="section"
      >
        <h2 className="section-title">Service Times</h2>
        <p className="section-subtitle">Join us for worship and fellowship at any of our weekly services</p>

        <div className="service-times-grid">
          {[
            { title: "Sunday Worship", time: "11:00 AM", desc: "Join us for inspiring worship, biblical teaching, and fellowship" },
            { title: "Tuesday Bible Study", time: "7:00 PM", desc: "Dive deeper into God's Word with our midweek study sessions" },
            { title: "Youth Service", time: "11:00 AM, Every Last Sunday", desc: "Dynamic gatherings for middle and high school students" }
          ].map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="time-card"
            >
              <h3>{service.title}</h3>
              <span className="time">{service.time}</span>
              <p>{service.desc}</p>
            </motion.div>
          ))}

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="time-card"
            style={{ background: 'var(--red-accent)', color: 'var(--white)', borderColor: 'var(--red-accent)' }}
          >
            <h3 style={{ color: 'var(--white)' }}>Watch Live Online</h3>
            <span className="time" style={{ color: 'var(--white)' }}>Sunday 11:00 AM</span>
            <p style={{ color: 'rgba(255,255,255,0.9)' }}>Can't make it in person? Join our livestream every Sunday</p>
          </motion.div>
        </div>
      </motion.section>

      {/* Stay Connected Social Media */}
      <motion.section
        initial={{ opacity: 0, rotateY: 15 }}
        whileInView={{ opacity: 1, rotateY: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8 }}
        className="social-section">
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h2 className="section-title c-size">Stay Connected</h2>
          <p className="section-subtitle">Follow us on social media to stay up to date with everything happening at RCCG Amazing Grace</p>

          <div className="social-grid">
            <MotionLink
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              to="https://www.youtube.com/@agcmiddletown" target="_blank" rel="noopener noreferrer" className="social-card">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              <h3>YouTube</h3>
              <p>Watch sermons & events</p>
            </MotionLink>

            <MotionLink
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              to="https://www.facebook.com/rccgmiddletownde.org/" target="_blank" rel="noopener noreferrer" className="social-card">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <h3>Facebook</h3>
              <p>Join our community</p>
            </MotionLink>

            <MotionLink
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              to="https://www.instagram.com/agcmiddletown/" target="_blank" rel="noopener noreferrer" className="social-card">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
              </svg>
              <h3>Instagram</h3>
              <p>See our latest photos</p>
            </MotionLink>
          </div>
        </div>
      </motion.section>

      {/* App Section */}
      <motion.section
        initial={{ opacity: 0, y: 80 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8 }}
        className="app-section"
      >
        <div className="app-content">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="app-info"
          >
            <h2>Download The App</h2>
            <p>Stay connected on the go. Get sermons, event updates, prayer requests, and more right on your phone.</p>

            <ul className="app-features">
              <li>Watch and listen to messages anytime</li>
              <li>Stay updated on events and news</li>
              <li>Give securely from your phone</li>
              <li>Access prayer requests and resources</li>
            </ul>

            <div className="app-buttons">
              <Link to="https://www.google.com/url?sa=t&source=web&rct=j&opi=89978449&url=https://apps.apple.com/ua/app/rccgagc/id1634455168&ved=2ahUKEwiU-bWx2cqSAxUmd0EAHf2iBGgQFnoECBoQAQ&usg=AOvVaw1oG7At2-00Sjox1q3KL8aT" target="_blank" rel="noopener noreferrer" className="app-button">
                <img src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&amp;releaseDate=1592179200" alt="Download on the App Store" />
              </Link>
              <Link to="https://www.google.com/url?sa=t&source=web&rct=j&opi=89978449&url=https://play.google.com/store/apps/details%3Fid%3Dcom.churchpad.rccgagc%26hl%3Den_GB&ved=2ahUKEwiU-bWx2cqSAxUmd0EAHf2iBGgQFnoECBsQAQ&usg=AOvVaw0rJPF-xUy3C2fZun693zEZ" target="_blank" rel="noopener noreferrer" className="app-button1">
                <img src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" alt="Get it on Google Play" />
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="app-mockup"
          >
            <div className="app-mockup-placeholder">
              <img src="https://res.cloudinary.com/dsnontai6/image/upload/v1770321941/unnamed_pcfxuc.jpg" alt="App Mockup" style={{ width: '100%', borderRadius: '30px' }} />
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Get In Touch */}
      <motion.section
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.7 }}
        className="section sec"
      >
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-subtitle">We're here to answer your questions and help you connect</p>

          <div className="contact-grid">
            {[
              { icon: "📍", title: "Visit Us", content: "211 E Main St\nMiddletown, DE 19709", link: "#map", linkText: "Get Directions" },
              { icon: "☎️", title: "Call Us", content: "Speak directly with our team during office hours", link: "tel:+13024650760", linkText: "(302) 465-0760" },
              { icon: "✉️", title: "Email Us", content: "Send us a message and we'll respond within 24 hours", link: "mailto:pr@rccgagcde.com", linkText: "pr@rccgagcde.com" }
            ].map((contact, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="contact-card"
              >
                <div className="contact-icon">{contact.icon}</div>
                <h3>{contact.title}</h3>
                <p>{contact.content.split('\n').map((line, i) => <span key={i}>{line}<br/></span>)}</p>
                <a href={contact.link}>{contact.linkText}</a>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Contact Form */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8 }}
        className="form-section"
      >
        <div className="form-container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="section-title">Send Us a Message</h2>
            <p className="section-subtitle">Fill out the form below and someone from our team will get back to you soon.</p>
          </div>

          <form onSubmit={sendEmail}>
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input type="text" id="name" name="user_name" placeholder="Your full name" required />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input type="email" id="email" name="user_email" placeholder="your@email.com" required />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone (Optional)</label>
              <input type="tel" id="phone" name="user_phone" placeholder="(123) 456-7890" />
            </div>

            <div className="form-group">
              <label htmlFor="subject">Subject</label>
              <select id="subject" name="user_subject" required>
                <option value="">Select a topic</option>
                <option value="General Inquiry">General Inquiry</option>
                <option value="Prayer Request">Prayer Request</option>
                <option value="Planning a Visit">Planning a Visit</option>
                <option value="Volunteer Opportunities">Volunteer Opportunities</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea id="message" name="user_message" placeholder="How can we help you?" required></textarea>
            </div>

            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>

          {/* Popup Message */}
          <AnimatePresence>
            {popupMessage && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="popup-overlay"
                onClick={closePopup}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: 20 }}
                  transition={{ duration: 0.3 }}
                  className={`popup-box ${popupType}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="popup-icon">
                    {popupType === 'success' ? (
                      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                        <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="3" />
                        <path d="M14 24L20 30L34 16" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                        <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="3" />
                        <path d="M16 16L32 32M32 16L16 32" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                      </svg>
                    )}
                  </div>
                  <p>{popupMessage}</p>
                  <button onClick={closePopup}>Close</button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.section>

      {/* Map Section */}
      <motion.section
        initial={{ opacity: 0, x: -60 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8 }}
        className="map-section"
        id="map"
      >
        <h2 className="section-title find-c">Find Us</h2>
        <p className="section-subtitle" style={{ textAlign: 'center' }}>We're located in the heart of Middletown, Delaware</p>
        <div className="map-container">
          <iframe
            src="https://www.google.com/maps?q=211+E+Main+St,+Middletown,+DE+19709&output=embed"
            allowFullScreen=""
            loading="lazy"
            title="RCCG Amazing Grace Location">
          </iframe>
        </div>
        <div className="map-address">
          <h3>Our Location</h3>
          <p><strong>211 E Main St, Middletown, DE 19709</strong></p>
          <p style={{ marginTop: '1rem' }}><strong>Office Hours:</strong> Monday - Friday, 9:00 AM - 5:00 PM</p>
          <p><strong>Sunday Services:</strong> 11:00 AM</p>
        </div>
      </motion.section>
    </div>
  );
};

export default Connect;
